import { DraggableElement } from '@recruitee/common';

import { User } from '../admin/admin.types';
import { Fieldset, FieldTemplate } from '../custom-fields/field.types';
import { Stage } from '../stage/stage.types';
import { PipelineTemplateItem } from './../pipeline-templates/pipeline-templates.types';
import { OfferKind, OfferStatus } from './offer.enums';

export type OfferLanguageItem = {
  translationFieldsCount: number;
  primary: boolean;
  nativeName: string;
  name: string;
  enabled: boolean;
  code: string;
  offerId?: number;
};

export type OfferStage = Partial<Stage> & DraggableElement;

export type Offer = {
  offerTags: string[];
  adminappUrl: string;
  admins: User[];
  attachmentsCount: number;
  candidatesCount: number;
  careersUrl: string;
  category: string | null;
  city: string;
  countryCode: string;
  createdAt: string;
  closedAt: string;
  defaultTranslations: { title: string; emailConfirmationSubject: string };
  department?: string;
  description: string;
  disqualifiedCandidatesCount: number;
  enabledForReferrals: boolean;
  enabledLanguages: Partial<OfferLanguageItem>[];
  newCandidatesCount?: number;
  education: string;
  emailConfirmation: true;
  emailConfirmationBody: string;
  emailConfirmationSubject: string;
  employmentType: string;
  experience: string;
  example?: boolean;
  followed: boolean;
  fields: FieldTemplate[];
  fieldset: Fieldset;
  hasAdditionalInfo: boolean;
  hasActiveCampaign: boolean;
  id: number;
  kind: TOfferKind;
  jobScheduler: JobSchedulerData;
  langCode: string;
  location: string;
  mailboxEmail: string;
  maxHours: number;
  minHours: number;
  notesCount: number;
  optionsCoverLetter: string;
  optionsCv: string;
  optionsPhone: string;
  optionsPhoto: string;
  pipeline: boolean;
  position: number;
  postalCode: string;
  primaryLangCode: string;
  publishedAt: string;
  qualifiedCandidatesCount: number;
  remote: boolean;
  requirements: string;
  slug: string;
  openQuestionTemplateId: number | null;
  stateCode: string;
  status: OfferStatus;
  title: string;
  updatedAt: string;
  url: string;
  visibilityOptions: string[];
  autoReplyTemplateId: number | null;
  pipelineTemplate: Omit<PipelineTemplateItem, 'assignedOffersCount'>;
  pipelineTemplateId: number;
  sharingTitle: string;
  sharingDescription: string;
  sharingImage: string;
};

export type OfferItem = Pick<
  Offer,
  | 'adminappUrl'
  | 'candidatesCount'
  | 'careersUrl'
  | 'city'
  | 'countryCode'
  | 'createdAt'
  | 'department'
  | 'disqualifiedCandidatesCount'
  | 'followed'
  | 'id'
  | 'kind'
  | 'location'
  | 'mailboxEmail'
  | 'pipeline'
  | 'position'
  | 'postalCode'
  | 'qualifiedCandidatesCount'
  | 'pipelineTemplate'
  | 'slug'
  | 'stateCode'
  | 'status'
  | 'title'
  | 'updatedAt'
  | 'url'
  | 'example'
>;

export type TOfferKind = `${OfferKind}`;
export type TOfferStatus = `${OfferStatus}`;

export type OfferGroup = {
  title: string;
  offers: OfferItem[];
};

export enum OfferViewMode {
  Default = 'default',
  Brief = 'brief',
}

export interface OfferData extends Partial<Offer> {
  adminIds?: number[];
  department?: string;
  description?: string;
  fields?: FieldTemplate[];
  fieldsetId?: number | null;
  id?: number;
  kind?: OfferKind;
  title?: string;
}

export type OfferLanguagesSetup = {
  translationFieldsCount: number;
  languages: OfferLanguageItem[];
};

export enum StatusChangeAction {
  Duplicate = 'duplicate',
  Unarchive = 'unarchive',
  Archive = 'archive',
  Delete = 'delete',
  Publish = 'publish',
  Unpublish = 'unpublish',
  Close = 'close',
  Draft = 'draft',
}

export enum PipelineSortOption {
  Name = 'name',
  Evaluation = 'positive_ratings',
  CreatedAt = 'created_at',
  New = 'viewed',
}

export type JobSchedulerData = {
  publishDate: string;
  closeDate: string;
  timezone: string;
  error?: string;
} | null;
